import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useMemo } from "react";
import color from "../../../../../constants/color";
import styles from "./style";
import { formatDate } from "../../../../../utils/DateFormat";
dayjs.extend(utc);

const ReconciliationDetailsPage = ({
  data,
  openingBalance,
  endDate,
  startDate,
  closingBalance,
  vendors,
  customers,
  bankName
}) => {
  const getId = (row, idx) => row._id ?? row.id ?? idx;

  const vendorMap = useMemo(() => {
    const map = {};
    vendors.forEach((v) => {
      map[v._id] = v.name;
    });
    return map;
  }, [vendors]);

  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach((c) => {
      map[c._id] = c.name;
    });
    return map;
  }, [customers]);

  return (
    <>
      <Box
        px={2}
        pt={2.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5" sx={styles.heading}>
          {bankName} - Reconciliation Details Page
        </Typography>
      </Box>

      <Box sx={{ backgroundColor: color.card, m: 2, borderRadius: "20px" }}>
        <Box sx={{ py: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.TableHead}>Date</TableCell>
                  <TableCell sx={styles.TableHead}>Description</TableCell>
                  <TableCell sx={styles.TableHead}>Type</TableCell>
                  <TableCell sx={styles.TableHead} align="right">
                    Deposits
                  </TableCell>
                  <TableCell sx={styles.TableHead} align="right">
                    Withdrawals
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="h6">No data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    <TableRow>
                      <TableCell sx={{ ...styles.tableData, fontWeight: 600 }}>
                        {startDate
                          ? formatDate(startDate)
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...styles.tableData,
                          maxWidth: 200,
                          minWidth: 120,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                        }}
                        title={"*** Opening Balance ***"}
                      >
                        *** Opening Balance ***
                      </TableCell>
                      <TableCell sx={styles.tableData}></TableCell>
                      <TableCell
                        sx={{ ...styles.tableData, fontWeight: 600 }}
                        align="right"
                      >
                        {`$${openingBalance.toLocaleString()}`}
                      </TableCell>
                      <TableCell sx={styles.tableData}></TableCell>
                    </TableRow>
                    {data.map((item, idx) => {
                      const rowId = getId(item, idx);
                      const vendorName = item.vendor
                        ? vendorMap[item.vendor._id || item.vendor] ||
                          item.vendor.name ||
                          item.vendor
                        : "";
                      const customerName = item.customer
                        ? customerMap[item.customer._id || item.customer] ||
                          item.customer.name ||
                          item.customer
                        : "";
                      return (
                        <TableRow key={rowId} hover sx={{ cursor: "pointer" }}>
                          <TableCell sx={styles.tableData}>
                            {dayjs.utc(item?.date).format("YYYY-MM-DD")}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...styles.tableData,
                              maxWidth: 200,
                              minWidth: 120,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={item.description}
                          >
                            {item?.description}
                          </TableCell>
                          <TableCell sx={styles.tableData}>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "13px",
                                  color: color.text,
                                }}
                              >
                                {item.vendor
                                  ? "Vendor Payment"
                                  : "Customer Payment"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: color.lightText,
                                }}
                              >
                                {item.vendor
                                  ? `Vendor: ${vendorName}`
                                  : item.customer
                                  ? `Customer: ${customerName}`
                                  : ""}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.tableData} align="right">
                            {item.deposit ? `$${item.deposit}` : ""}
                          </TableCell>
                          <TableCell sx={styles.tableData} align="right">
                            {item.withdrawal ? `$${item.withdrawal}` : ""}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell sx={{ ...styles.tableData, fontWeight: 600 }}>
                        {endDate
                          ? formatDate(endDate)
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...styles.tableData,
                          maxWidth: 200,
                          minWidth: 120,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                        }}
                        title={"*** Closing Balance ***"}
                      >
                        *** Closing Balance ***
                      </TableCell>
                      <TableCell sx={styles.tableData}></TableCell>
                      <TableCell
                        sx={{ ...styles.tableData, fontWeight: 600 }}
                        align="right"
                      >
                        {`$${closingBalance.toLocaleString()}`}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default ReconciliationDetailsPage;
